export class Formatters {
  static escapeHtml(str: string | null | undefined): string {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Non-finite (NaN/Infinity) as well as null/undefined fall back to '0' so a
  // failed computation never surfaces the literal string "NaN".
  static formatNumber(n: number | null | undefined): string {
    if (!Number.isFinite(n as number)) return '0';
    return Math.round(n as number).toLocaleString();
  }

  static formatCurrency(amount: number | null | undefined): string {
    if (!Number.isFinite(amount as number)) return '$0';
    return `$${this.formatNumber(amount)}`;
  }

  static formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '--';
    try {
      // Parse YYYY-MM-DD as local date to avoid UTC→local shift (new Date('YYYY-MM-DD') = UTC midnight)
      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const d = match
        ? new Date(+match[1], +match[2] - 1, +match[3])
        : new Date(dateStr);
      // toLocaleDateString on an invalid Date returns the literal "Invalid Date"
      // (it does not throw), so the try/catch never fires — guard explicitly.
      if (Number.isNaN(d.getTime())) return '--';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '--';
    }
  }

  static formatPercent(value: number | null | undefined, decimals: number = 1): string {
    if (!Number.isFinite(value as number)) return '--';
    return `${(value as number).toFixed(decimals)}%`;
  }

  // THE health-score bands (80/60/40) for every page. The server derives its
  // status strings from the same cutoffs when scoring (server.js healthScore →
  // critical/needs-attention/fair/healthy) — change both together or pages and
  // API payloads will disagree about the same listing.
  static getHealthColor(score: number): 'good' | 'fair' | 'warning' | 'critical' {
    if (score >= 80) return 'good';
    if (score >= 60) return 'fair';
    if (score >= 40) return 'warning';
    return 'critical';
  }

  // Hex + status-label views of the same bands, for pages that paint inline.
  static getHealthHex(score: number): string {
    const hex = { good: '#16a34a', fair: '#65a30d', warning: '#f59e0b', critical: '#dc2626' };
    return hex[Formatters.getHealthColor(score)];
  }
  static getHealthStatus(score: number): 'healthy' | 'fair' | 'needs-attention' | 'critical' {
    const status = { good: 'healthy', fair: 'fair', warning: 'needs-attention', critical: 'critical' } as const;
    return status[Formatters.getHealthColor(score)];
  }

  // Priority-score bands (70/40) — a different metric than health, with
  // intentionally different cutoffs. Keep TriagePage chips in sync with this.
  static getPriorityColor(score: number): 'critical' | 'warning' | 'low' {
    if (score >= 70) return 'critical';
    if (score >= 40) return 'warning';
    return 'low';
  }

  static getChannelClass(channel: string | null | undefined): string {
    if (!channel) return 'channel-other';
    const lower = channel.toLowerCase();
    if (lower.includes('airbnb')) return 'channel-airbnb';
    if (lower.includes('vrbo') || lower.includes('homeaway')) return 'channel-vrbo';
    if (lower.includes('booking')) return 'channel-booking';
    if (lower.includes('direct')) return 'channel-direct';
    return 'channel-other';
  }
}
